import React, { FC, useState } from "react";
import DialogLayout from "../DialogLayout";
import Typography from "../../components/Typography/Typography";
import ASSETS from "../../assets";
import HumanVerificationModal from "./HumanVerificationModal";
import { VerificationLevel } from "@worldcoin/idkit";
import { ISuccessResult } from "@worldcoin/idkit-core";
import { useSelector, useDispatch } from "react-redux";
import { RootState, Dispatch } from "../../store";

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onBack?: () => void;
  onVerificationComplete?: () => void;
  onCloseAll?: () => void;
}

interface VerificationOption {
  id: string;
  label: string;
  icon: string;
  alt: string;
  verificationLevel: VerificationLevel;
}
// World ID configuration
const WORLD_ID_CONFIG = {
  app_id: 'app_54cc933aed5b7b7947611f2dd401f2f3' as const,
  action: 'proof-of-human',
  signal: 'test_signal',
  partner: false
};

const VerificationMethod: FC<IProps> = ({ open, setOpen, onBack, onVerificationComplete, onCloseAll }) => {
  const [selectedMethod, setSelectedMethod] = useState<VerificationOption | null>(null);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const { user } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch<Dispatch>();
  const verificationOptions: VerificationOption[] = [
    {
      id: "device",
      label: "Device Verification",
      icon: ASSETS.device,
      alt: "Device",
      verificationLevel: VerificationLevel.Device
    },
    {
      id: "orb",
      label: "Orb Verification",
      icon: ASSETS.union,
      alt: "Orb",
      verificationLevel: VerificationLevel.Orb
    },
   
  ];



  const appStoreLinks = [
    {
      src: ASSETS.apple,
      alt: "App Store",
      url: "https://apps.apple.com/no/app/world-app-worldcoin-wallet/id1560859847?utm_source=chatgpt.com"
    },
    {
      src: ASSETS.google,
      alt: "Google Play",
      url: "https://play.google.com/store/apps/details?hl=en_US&id=com.worldcoin&utm_source=chatgpt.com"
    }
  ];
  const handleClose = () => {
    if (onCloseAll) {
      onCloseAll();
    } else {
      setOpen(false);
      setSelectedMethod(null);
      setShowVerificationModal(false);
    }
  };

  const handleBack = () => {
    if (showVerificationModal) {
      setShowVerificationModal(false);
    } else if (selectedMethod) {
      setSelectedMethod(null);
    } else {
      onBack ? onBack() : handleClose();
    }
  };

  const handleVerificationMethod = (method: VerificationOption) => {
    setSelectedMethod(method);
    setShowVerificationModal(true);
  };

  const handleVerificationSuccess = async (result: ISuccessResult) => {
    console.log("Verification successful:", result);
    
    try {
      // Refresh user data to get updated verification status
      await dispatch.auth.handleGetCurrentUser();
      
      // Close all modals
      handleClose();
      
      // Call the callback to open Profile modal
      if (onVerificationComplete) {
        onVerificationComplete();
      }
    } catch (error) {
      console.error("Failed to refresh user data:", error);
      // Still close the modal even if refresh fails
      handleClose();
      // Call the callback to open Profile modal even if refresh fails
      if (onVerificationComplete) {
        onVerificationComplete();
      }
    }
  };

  const handleVerificationError = (error: any) => {
    console.error("Verification failed:", error);
    // You can add additional error handling here
    // For example, show an error message
  };

  const handleAppStoreClick = (url: string) => {
    window.open(url, "_blank");
  };

  return (
    <DialogLayout open={open}>
      <div className="w-full max-w-[532px] py-6 px-10 bg-[#F2F2F7] rounded-[15px] relative ">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <button
            onClick={handleBack}
            className="text-blue-600 font-bold text-sm hover:underline flex items-center gap-1"
          >
            <img src={ASSETS.arrow_left} alt="Back" className="w-3 h-3" />
            Back
          </button>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200 transition-colors"
          >
            <img src={ASSETS.cross} alt="Close" className="w-4 h-4" />
          </button>
        </div>

        {/* Title */}
        <Typography variant="h2" className="font-bold title">
          Verification Method
        </Typography>
        <Typography variant="body1" color="grey" className="text-sm mb-8">
          Please select the method you used in the past to verify yourself in the World App.
        </Typography>

        {/* Verification Options */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6">
          {verificationOptions.map((option, index) => {
            const isUserLevel = user?.verificationLevel === option.id;
            return (
              <button
                key={option.id}
                onClick={() => !isUserLevel && handleVerificationMethod(option)}
                disabled={isUserLevel}
                className={`w-full p-6 flex items-center justify-between transition-colors ${
                  isUserLevel ? "opacity-500 cursor-not-allowed" : "hover:bg-gray-50"
                } ${index < verificationOptions.length - 1 ? "border-b border-gray-200" : ""
                  }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center">
                    <img src={option.icon} alt={option.alt} className="w-5 h-5" />
                  </div>
                  <Typography variant="body1" className="font-bold text-black text-[14px]">
                    {option.label}
                  </Typography>
                  {isUserLevel && (
                    <img src={ASSETS.greenCircle} alt="Verified" className="w-4 h-4 " />
                  )}
                </div>
                <img src={ASSETS.arrow_right_grey} alt="Arrow" className="w-4 h-4" />
              </button>
            );
          })}

          <div className="text-center mt-[234px] mb-[15px]">
            <Typography variant="body1" color="grey" className="text-sm mb-2 font-semibold">
              Don't have World ID?
            </Typography>
            <div className="flex justify-center gap-2">
              {appStoreLinks.map((link) => (
                <img
                  key={link.alt}
                  src={link.src}
                  alt={link.alt}
                  className="w-32 h-12 cursor-pointer"
                  onClick={() => handleAppStoreClick(link.url)}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Human Verification Modal */}
      {selectedMethod && (
        <HumanVerificationModal
          open={showVerificationModal}
          setOpen={setShowVerificationModal}
          onBack={() => setShowVerificationModal(false)}
          onCloseAll={onCloseAll}
          app_id={WORLD_ID_CONFIG.app_id}
          action={WORLD_ID_CONFIG.action}
          signal={WORLD_ID_CONFIG.signal}
          partner={WORLD_ID_CONFIG.partner}
          verificationLevel={selectedMethod.verificationLevel}
          onVerificationSuccess={handleVerificationSuccess}
          onVerificationError={handleVerificationError}
        />
      )}
    </DialogLayout>
  );
};

export default VerificationMethod;
